// Client-side status classification — the offline / pre-first-tick
// fallback and the optimistic-completion overlay.
//
// The cooking screen renders off a `ClassifiedPlan`. Authoritatively that
// comes from the server (`/tick`, `/complete-task`), where the soft/hard
// overrun thresholds are the IP. But the server is not always there: on
// plain `vite dev` the functions are absent, and even under `netlify dev`
// there is a window before the first tick returns. README §9 ("degrade to
// last-known-good … client interpolates timers off the cached schedule")
// and §10.2 ("the client can run the whole cook from the cached anchored
// schedule if the network drops") expect the client to keep going.
//
// The basic split is a pure wall-clock comparison, identical to the
// function's `classifyStatus`:
//   completedTaskIds.includes(t.id)        → done
//   now < t.startAt                        → upcoming
//   t.startAt ≤ now < t.mustCompleteBy     → active
//   t.mustCompleteBy ≤ now                 → overrun
// Banner depth mirrors the server: < 2 overruns → soft, else hard.

import type {
  BannerMode,
  ClassifiedPlan,
  ClassifiedTask,
  ScheduleEntry,
  TaskState,
} from '@cookchow/shared';

function classifyOne(t: ScheduleEntry, nowMs: number, completed: Set<string>): TaskState {
  if (completed.has(t.id)) return 'done';
  const startMs = Date.parse(t.startAt);
  const mustMs = Date.parse(t.mustCompleteBy);
  if (Number.isFinite(startMs) && nowMs < startMs) return 'upcoming';
  if (Number.isFinite(mustMs) && nowMs < mustMs) return 'active';
  return 'overrun';
}

function summarise(tasks: ClassifiedTask[]): ClassifiedPlan {
  const overrunCount = tasks.reduce((n, t) => (t.state === 'overrun' ? n + 1 : n), 0);
  const bannerMode: BannerMode = overrunCount < 2 ? 'soft' : 'hard';
  // "Most recently entered active" = the last active task in order.
  let activeTask: ScheduleEntry | null = null;
  for (const t of tasks) {
    if (t.state === 'active') activeTask = t.task;
  }
  let livecookEligible = true;
  for (const t of tasks) {
    if ((t.task.phase === 'preCook' || t.task.phase === 'prepCook') && t.state !== 'done') {
      livecookEligible = false;
      break;
    }
  }
  return { tasks, overrunCount, bannerMode, activeTask, livecookEligible };
}

/** Compute a full ClassifiedPlan from the immutable schedule + wall-clock. */
export function classifyLocal(
  schedule: readonly ScheduleEntry[],
  nowMs: number,
  completedTaskIds: readonly string[],
): ClassifiedPlan {
  const completed = new Set(completedTaskIds);
  const tasks: ClassifiedTask[] = schedule.map((task) => ({
    task,
    state: classifyOne(task, nowMs, completed),
  }));
  return summarise(tasks);
}

/**
 * Overlay optimistic completion onto an existing plan: any task the user
 * has checked off is forced to `done` immediately, without waiting for
 * the server to confirm. Returns the same reference when nothing changes
 * so consumers can keep referential-equality memoisation.
 */
export function withOptimisticCompletion(
  plan: ClassifiedPlan,
  completedTaskIds: readonly string[],
): ClassifiedPlan {
  const completed = new Set(completedTaskIds);
  let changed = false;
  const tasks = plan.tasks.map((ct) => {
    if (completed.has(ct.task.id) && ct.state !== 'done') {
      changed = true;
      return { task: ct.task, state: 'done' as const };
    }
    return ct;
  });
  return changed ? summarise(tasks) : plan;
}
